import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import DashboardIcon from '@mui/icons-material/Dashboard';
import BarChartIcon from '@mui/icons-material/BarChart';
import RedeemIcon from '@mui/icons-material/Redeem';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import NotificationsIcon from '@mui/icons-material/Notifications';
import LogoutIcon from '@mui/icons-material/Logout';
import logo from '../images/logo.png';
import { useAuth } from '../hooks/useAuth';
import { apiPost } from '../api/client';
import { getNotificationsCount } from '../api/endorsements';
import ColorModeToggle from './ColorModeToggle';
import { PURPLE_AVATAR, PURPLE_FOOTER } from '../theme/tokens';

const NAV_LINK_SX = {
    color: 'rgba(255,255,255,0.9)',
    textDecoration: 'none',
    px: 1.5,
    py: 0.5,
    borderRadius: 1,
    fontSize: '0.875rem',
    fontWeight: 500,
    '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.12)' },
    '&.active': { color: '#fff', bgcolor: 'rgba(255,255,255,0.18)' },
};

export default function Layout() {
    const { user, logout, refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [aboutAnchor, setAboutAnchor] = useState<null | HTMLElement>(null);
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
    const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
    const [notificationCount, setNotificationCount] = useState(0);

    // Close dropdown on navigation
    useEffect(() => { setAboutAnchor(null); }, [location.pathname]);

    // Poll the bell-icon count; refresh on route change so resolving a row on
    // /notifications drops the badge without a hard reload.
    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const load = async () => {
            const res = await getNotificationsCount();
            if (!cancelled && res.success) setNotificationCount(res.data?.totalCount ?? 0);
        };
        void load();
        const id = window.setInterval(load, 60_000);
        return () => { cancelled = true; window.clearInterval(id); };
    }, [user, location.pathname]);

    const handleLogout = async () => {
        setUserMenuAnchor(null);
        await logout();
        navigate('/login');
    };

    const handleResendVerification = async () => {
        setResendState('sending');
        const res = await apiPost<void>('/auth/resend-verification', {});
        setResendState(res.success ? 'sent' : 'error');
    };

    const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
    const handleStopImpersonation = async () => {
        setStoppingImpersonation(true);
        try {
            const res = await apiPost<void>('/auth/impersonate/stop', {});
            if (res.success) {
                await refresh();
                navigate('/dashboard');
            } else {
                alert(res.message ?? 'Failed to return to admin account.');
            }
        } finally {
            setStoppingImpersonation(false);
        }
    };

    return (
        <Box display="flex" flexDirection="column" minHeight="100vh">
            {/* Navbar */}
            <AppBar
                component="nav"
                position="sticky"
            >
                <Toolbar
                    sx={{
                        justifyContent: 'flex-start',
                        minHeight: 48,
                        position: 'relative',
                    }}
                >
                    <NavLink
                        to="/"
                        style={{ display: 'flex', marginRight: 24 }}
                    >
                        <Box
                            component="img"
                            src={logo}
                            alt="TC Everywhere"
                            sx={{ maxHeight: 32, maxWidth: '90vw', width: 'auto' }}
                        />
                    </NavLink>

                    <Box component="ul" display="flex" alignItems="center" gap={1} sx={{ listStyle: 'none', m: 0, p: 0, flexGrow: 1 }}>
                        <li>
                            <Button
                                component={NavLink}
                                to="/badges/create"
                                variant="contained"
                                startIcon={<AddIcon />}
                            >
                                Create a Badge
                            </Button>
                        </li>
                        <li><Box component={NavLink} to="/badges" sx={NAV_LINK_SX}>Manage Badges</Box></li>
                        <li><Box component={NavLink} to="/available-badges" sx={NAV_LINK_SX}>Discover</Box></li>
                        <li><Box component={NavLink} to="/issuers" sx={NAV_LINK_SX}>My Issuers</Box></li>
                        <li>
                            <Button
                                variant="text"
                                onClick={(e) => setAboutAnchor(e.currentTarget)}
                                endIcon={<KeyboardArrowDownIcon />}
                                sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}
                            >
                                About
                            </Button>
                        </li>
                    </Box>

                    <Box sx={{ position: 'absolute', right: 16, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ColorModeToggle />
                        <IconButton
                            aria-label="Notifications"
                            onClick={() => navigate('/notifications')}
                            size="small"
                            sx={{ color: '#fff' }}
                        >
                            <Badge color="error" badgeContent={notificationCount} overlap="circular">
                                <NotificationsIcon />
                            </Badge>
                        </IconButton>
                        <IconButton
                            aria-label="Account menu"
                            onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                            size="small"
                        >
                            <Avatar
                                src={user?.profileImage ?? undefined}
                                alt={user?.displayName}
                                sx={{ width: 36, height: 36, bgcolor: PURPLE_AVATAR }}
                            >
                                {!user?.profileImage && (user?.firstName?.[0] ?? '?')}
                            </Avatar>
                        </IconButton>
                        <Menu
                            anchorEl={userMenuAnchor}
                            open={Boolean(userMenuAnchor)}
                            onClose={() => setUserMenuAnchor(null)}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            {user?.isAdmin ? (
                                <MenuItem component={NavLink} to="/dashboard" onClick={() => setUserMenuAnchor(null)}>
                                    <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                                    Dashboard
                                </MenuItem>
                            ) : (
                                <MenuItem component={NavLink} to="/home" onClick={() => setUserMenuAnchor(null)}>
                                    <ListItemIcon><DashboardIcon fontSize="small" /></ListItemIcon>
                                    Dashboard
                                </MenuItem>
                            )}
                            {user?.isAdmin && (
                                <MenuItem component={NavLink} to="/reports" onClick={() => setUserMenuAnchor(null)}>
                                    <ListItemIcon><BarChartIcon fontSize="small" /></ListItemIcon>
                                    Reports
                                </MenuItem>
                            )}
                            <MenuItem component={NavLink} to="/issued" onClick={() => setUserMenuAnchor(null)}>
                                <ListItemIcon><RedeemIcon fontSize="small" /></ListItemIcon>
                                Issued Badges
                            </MenuItem>
                            <MenuItem component={NavLink} to="/portfolio" onClick={() => setUserMenuAnchor(null)}>
                                <ListItemIcon><WorkspacePremiumIcon fontSize="small" /></ListItemIcon>
                                My Portfolio
                            </MenuItem>
                            <MenuItem component={NavLink} to="/notifications" onClick={() => setUserMenuAnchor(null)}>
                                <ListItemIcon><NotificationsIcon fontSize="small" /></ListItemIcon>
                                <Box flexGrow={1}>Notifications</Box>
                                {notificationCount > 0 && (
                                    <Chip
                                        size="small"
                                        color="error"
                                        label={notificationCount}
                                        sx={{ ml: 2, height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.75rem' } }}
                                    />
                                )}
                            </MenuItem>
                            <MenuItem component={NavLink} to="/profile" onClick={() => setUserMenuAnchor(null)}>
                                <ListItemIcon><AccountCircleIcon fontSize="small" /></ListItemIcon>
                                My Profile
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={handleLogout}>
                                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                                Logout
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>

                {/* Shared About menu */}
                <Menu
                    anchorEl={aboutAnchor}
                    open={Boolean(aboutAnchor)}
                    onClose={() => setAboutAnchor(null)}
                >
                    <MenuItem component={NavLink} to="/about" onClick={() => setAboutAnchor(null)}>About TC Everywhere</MenuItem>
                    <MenuItem component={NavLink} to="/how-it-works" onClick={() => setAboutAnchor(null)}>How It Works</MenuItem>
                    <MenuItem component={NavLink} to="/audience" onClick={() => setAboutAnchor(null)}>Who Is It For?</MenuItem>
                    <MenuItem component={NavLink} to="/why-free" onClick={() => setAboutAnchor(null)}>Why Is This Free?</MenuItem>
                    <MenuItem divider />
                    <MenuItem component={NavLink} to="/roadmap" onClick={() => setAboutAnchor(null)}>Road Map</MenuItem>
                    <MenuItem component={NavLink} to="/partners" onClick={() => setAboutAnchor(null)}>Partners</MenuItem>
                    <MenuItem component={NavLink} to="/developers" onClick={() => setAboutAnchor(null)}>Developers &amp; Integrations</MenuItem>
                    <MenuItem divider />
                    <MenuItem component={NavLink} to="/faqs" onClick={() => setAboutAnchor(null)}>FAQs</MenuItem>
                    <MenuItem component={NavLink} to="/contact" onClick={() => setAboutAnchor(null)}>Contact Us</MenuItem>
                </Menu>
            </AppBar>

            {/* Impersonation banner — shown while an admin is viewing the app as another user */}
            {user?.impersonator && (
                <Alert
                    severity="info"
                    sx={{ borderRadius: 0, justifyContent: 'center' }}
                    action={
                        <Button
                            variant="text"
                            color="inherit"
                            disabled={stoppingImpersonation}
                            onClick={handleStopImpersonation}
                        >
                            {stoppingImpersonation ? 'Returning…' : 'Return to admin'}
                        </Button>
                    }
                >
                    You are impersonating {user.displayName} ({user.email}) on behalf of {user.impersonator.displayName}.
                </Alert>
            )}

            {/* Email verification banner */}
            {user && !user.emailVerified && (
                <Alert
                    severity="warning"
                    sx={{ borderRadius: 0, justifyContent: 'center' }}
                    action={
                        <Button
                            variant="text"
                            color="inherit"
                            disabled={resendState === 'sending' || resendState === 'sent'}
                            onClick={handleResendVerification}
                        >
                            {resendState === 'sent' ? 'Email sent!' : resendState === 'sending' ? 'Sending…' : 'Resend email'}
                        </Button>
                    }
                >
                    Please verify your email address. Check your inbox for a confirmation link.
                </Alert>
            )}

            {/* Main content */}
            <Box component="main" flexGrow={1}>
                <Outlet />
            </Box>

            {/* Footer */}
            <Box
                component="footer"
                sx={{ bgcolor: PURPLE_FOOTER, color: 'rgba(255,255,255,0.5)', py: 2, textAlign: 'center', fontSize: '0.875rem' }}
            >
                TC Everywhere © {new Date().getFullYear()}
            </Box>
        </Box>
    );
}
